import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/api';
import NearbyComuniMap from './NearbyComuniMap';
import { MapTrifold, X } from '@phosphor-icons/react';

const ComuneRow = ({ it, area, onToggle, onToggleCap }) => (
  <div className={`rounded-lg border p-2.5 ${it.taken_by ? 'bg-slate-50 opacity-70' : area ? 'border-primary bg-primary/5' : 'border-border'}`}
    data-testid={`nearby-row-${it.comune}`}>
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={!!area} disabled={!!it.taken_by} onChange={() => onToggle(it)}
        className="accent-[#1565C0]" data-testid={`nearby-check-${it.comune}`} />
      <span className="font-medium">{it.comune}</span>
      <span className="text-xs text-foreground/50">({it.provincia}) · {it.distance_km} km · {it.caps.length} CAP</span>
    </label>
    {it.taken_by && <div className="text-[11px] text-foreground/60 ml-6">Già coperto da {it.taken_by}</div>}
    {area && it.caps.length > 1 && (
      <div className="flex flex-wrap gap-1 ml-6 mt-1.5">
        {it.caps.map((cap) => (
          <button key={cap} type="button" onClick={() => onToggleCap(it.comune, cap)} data-testid={`nearby-cap-${it.comune}-${cap}`}
            className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${area.caps.includes(cap) ? 'bg-primary text-white border-primary' : 'bg-white text-foreground/60 border-border'}`}>
            {cap}
          </button>
        ))}
      </div>
    )}
  </div>
);

/** Centri in un comune con un solo CAP: aggiunta dei comuni entro 15 km dalla sede. */
export const NearbyComuniPanel = ({ comune, address, value, onChange, compact }) => {
  const [data, setData] = useState(null);
  const lat = address?.valid ? address.latitude : null;
  const lng = address?.valid ? address.longitude : null;

  useEffect(() => {
    if (!comune) { setData(null); return; }
    const qs = new URLSearchParams({ comune, ...(lat && lng ? { lat, lng } : {}) });
    api.get(`/api/coverage/nearby-comuni?${qs}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [comune, lat, lng]);

  const toggle = useCallback((it) => {
    const exists = value.some((a) => a.comune === it.comune);
    onChange(exists ? value.filter((a) => a.comune !== it.comune)
      : [...value, { region: it.regione, province: it.provincia, comune: it.comune, caps: [...it.caps] }]);
  }, [value, onChange]);

  const toggleCap = (name, cap) => onChange(value.map((a) => {
    if (a.comune !== name) return a;
    const caps = a.caps.includes(cap) ? a.caps.filter((c) => c !== cap) : [...a.caps, cap];
    return { ...a, caps };
  }).filter((a) => a.caps.length));

  if (!data?.eligible) return null;
  const items = data.items || [];
  return (
    <div className="mt-5 pt-5 border-t space-y-3" data-testid="nearby-comuni-panel">
      <div className="flex items-start gap-2">
        <MapTrifold size={20} weight="duotone" className="text-primary mt-0.5" />
        <div>
          <p className="font-heading font-medium text-sm">Comuni vicini (entro {data.radius_km} km)</p>
          <p className="text-xs text-foreground/60">
            {comune} ha un solo CAP: puoi estendere la copertura ai comuni entro {data.radius_km} km in linea d'aria
            {data.origin_source === 'sede' ? ' dalla sede verificata' : ' dal centro del comune (verifica l\'indirizzo per misurare dalla sede)'}.
            I comuni già coperti da un altro Centro non sono selezionabili.
          </p>
        </div>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="nearby-selected">
          {value.map((a) => (
            <span key={a.comune} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary text-white text-xs">
              {a.comune} · {a.caps.length} CAP
              <button type="button" onClick={() => onChange(value.filter((x) => x.comune !== a.comune))} aria-label={`Rimuovi ${a.comune}`}
                data-testid={`nearby-remove-${a.comune}`}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-xs text-foreground/60" data-testid="nearby-empty">Nessun comune entro {data.radius_km} km.</p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-3">
          <div className="rounded-xl overflow-hidden border border-border">
            <NearbyComuniMap origin={data.origin} radiusKm={data.radius_km} items={items}
              selected={value.map((a) => a.comune)} onToggle={toggle} height={compact ? 260 : 340} />
          </div>
          <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: compact ? 260 : 340 }} data-testid="nearby-list">
            {items.map((it) => (
              <ComuneRow key={it.comune} it={it} area={value.find((a) => a.comune === it.comune)} onToggle={toggle} onToggleCap={toggleCap} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NearbyComuniPanel;
